import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Plus, Send, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { getCompanySettings, getEstimatePdf, saveEstimate, sendEstimate } from '../../services/api';
import {
  TPS_RATE_LABEL, TVQ_RATE_LABEL, emptyLine, estimateTotals, formatCents, lineCents, lineErrors, toPayloadLines,
} from '../../lib/estimate';

const fromEstimate = (estimate) => ({
  title: estimate?.title || '',
  notes: estimate?.notes || '',
  valid_days: String(estimate?.valid_days || 30),
  taxable: estimate?.taxable ?? true,
  lines: estimate?.lines?.length
    ? estimate.lines.map((line) => ({
      description: line.description,
      quantity: String(line.quantity).replace('.', ','),
      unit_price: String(line.unit_price).replace('.', ','),
    }))
    : [emptyLine()],
});

const detailOf = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

// Devis chiffré d'une soumission : lignes, taxes, PDF et envoi au client.
export const EstimateDialog = ({ quote, open, onOpenChange, onChanged, canEdit }) => {
  const [form, setForm] = useState(() => fromEstimate(quote?.estimate));
  const [estimate, setEstimate] = useState(quote?.estimate || null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendTo, setSendTo] = useState(quote?.email || '');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (open) {
      setForm(fromEstimate(quote?.estimate));
      setEstimate(quote?.estimate || null);
      setDirty(false);
      setSendTo(quote?.email || '');
      setMessage('');
    }
  }, [open, quote]);

  // Nouveau devis : conditions et validité par défaut des Paramètres.
  const hasEstimate = Boolean(quote?.estimate);
  useEffect(() => {
    if (!open || hasEstimate || !canEdit) return undefined;
    let active = true;
    getCompanySettings()
      .then(({ data }) => {
        if (!active) return;
        setForm((current) => ({
          ...current,
          notes: current.notes || data.estimate_notes || '',
          valid_days: String(data.estimate_valid_days || current.valid_days),
        }));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [open, hasEstimate, canEdit]);

  const totals = useMemo(() => estimateTotals(form.lines, form.taxable), [form.lines, form.taxable]);

  const update = (changes) => {
    setForm((current) => ({ ...current, ...changes }));
    setDirty(true);
  };
  const updateLine = (index, changes) =>
    update({ lines: form.lines.map((line, position) => (position === index ? { ...line, ...changes } : line)) });

  const persist = async () => {
    const invalid = form.lines.findIndex((line) => lineErrors(line).length);
    if (invalid >= 0) {
      toast.error(`Ligne ${invalid + 1} : ${lineErrors(form.lines[invalid]).join(', ')} à corriger`);
      return null;
    }
    const days = parseInt(form.valid_days, 10);
    const { data } = await saveEstimate(quote.id, {
      title: form.title.trim() || null,
      notes: form.notes.trim() || null,
      valid_days: Number.isFinite(days) && days > 0 ? Math.min(days, 365) : 30,
      taxable: form.taxable,
      lines: toPayloadLines(form.lines),
    });
    setEstimate(data);
    setDirty(false);
    onChanged?.({ ...quote, estimate: data });
    return data;
  };

  const run = async (action, fallback) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.error(detailOf(error, fallback));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = () => run(async () => {
    if (await persist()) toast.success('Devis enregistré');
  }, "Erreur lors de l'enregistrement");

  const handlePreview = () => {
    // Fenêtre ouverte tout de suite pour ne pas être bloquée (iPhone, Safari).
    const preview = window.open('', '_blank');
    run(async () => {
      try {
        if ((dirty || !estimate) && canEdit && !(await persist())) {
          preview?.close();
          return;
        }
        const { data } = await getEstimatePdf(quote.id);
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        if (preview) preview.location.href = url;
        else window.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (error) {
        preview?.close();
        throw error;
      }
    }, "Impossible d'ouvrir le PDF");
  };

  const handleSend = () => run(async () => {
    if (!sendTo.trim()) {
      toast.error('Indiquez le courriel du client');
      return;
    }
    if ((dirty || !estimate) && !(await persist())) return;
    const { data } = await sendEstimate(quote.id, { email: sendTo.trim(), message: message.trim() || null });
    toast.success(`Soumission envoyée à ${data.sent_to}`);
    const sent = { ...(estimate || {}), status: 'sent', sent_at: data.sent_at, sent_to: data.sent_to };
    setEstimate(sent);
    onChanged?.({
      ...quote,
      estimate: sent,
      status: ['pending', 'contacted', undefined, null].includes(quote.status) ? 'quoted' : quote.status,
    });
  }, "Le courriel n'a pas pu être envoyé");

  if (!quote) return null;
  const clientName = [quote.first_name, quote.last_name].filter(Boolean).join(' ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="estimate-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Devis {estimate?.number ? <span className="text-[#00b4d8]">{estimate.number}</span> : null}
            <span className="text-sm font-normal text-slate-500">· {clientName}</span>
          </DialogTitle>
        </DialogHeader>

        {estimate?.sent_at && (
          <p className="text-sm rounded-lg bg-green-50 text-green-800 px-3 py-2">
            Envoyé à {estimate.sent_to} le {new Date(estimate.sent_at).toLocaleDateString('fr-CA')}
            {estimate.status !== 'sent' || dirty ? ' · modifié depuis l\'envoi' : ''}
          </p>
        )}

        <div className="space-y-5">
          <div>
            <Label>Titre</Label>
            <Input
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Ex. : Installation d'une thermopompe murale"
              maxLength={200}
              disabled={!canEdit}
            />
          </div>

          <div className="space-y-3">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 px-1">
              <span className="col-span-6">Description</span>
              <span className="col-span-2 text-right">Qté</span>
              <span className="col-span-2 text-right">Prix unitaire</span>
              <span className="col-span-2 text-right">Montant</span>
            </div>
            {form.lines.map((line, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 items-start rounded-lg border border-slate-200 p-2 sm:border-0 sm:p-0"
                data-testid={`estimate-line-${index}`}
              >
                <Textarea
                  value={line.description}
                  onChange={(e) => updateLine(index, { description: e.target.value })}
                  placeholder="Produit, service ou main-d'œuvre"
                  rows={1}
                  maxLength={500}
                  className="col-span-12 sm:col-span-6 min-h-[40px]"
                  disabled={!canEdit}
                  data-testid={`estimate-description-${index}`}
                />
                <Input
                  value={line.quantity}
                  onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  inputMode="decimal"
                  aria-label="Quantité"
                  className="col-span-3 sm:col-span-2 text-right"
                  disabled={!canEdit}
                  data-testid={`estimate-quantity-${index}`}
                />
                <Input
                  value={line.unit_price}
                  onChange={(e) => updateLine(index, { unit_price: e.target.value })}
                  inputMode="decimal"
                  placeholder="0,00"
                  aria-label="Prix unitaire"
                  className="col-span-4 sm:col-span-2 text-right"
                  disabled={!canEdit}
                  data-testid={`estimate-price-${index}`}
                />
                <div className="col-span-5 sm:col-span-2 flex items-center justify-end gap-1 h-10">
                  <span className="text-sm font-medium text-[#0f172a] whitespace-nowrap">{formatCents(lineCents(line))}</span>
                  {canEdit && form.lines.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                      onClick={() => update({ lines: form.lines.filter((_, position) => position !== index) })}
                      aria-label="Retirer la ligne"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => update({ lines: [...form.lines, emptyLine()] })}
                disabled={form.lines.length >= 100}
                data-testid="estimate-add-line"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter une ligne
              </Button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-6 sm:items-start">
            <div className="flex-1 space-y-4">
              <div>
                <Label>Notes et conditions</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => update({ notes: e.target.value })}
                  placeholder="Garantie, acompte, délais, exclusions..."
                  maxLength={4000}
                  disabled={!canEdit}
                />
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <Label htmlFor="valid-days" className="whitespace-nowrap">Valide (jours)</Label>
                  <Input
                    id="valid-days"
                    value={form.valid_days}
                    onChange={(e) => update({ valid_days: e.target.value.replace(/\D/g, '') })}
                    inputMode="numeric"
                    className="w-20"
                    disabled={!canEdit}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="taxable"
                    type="checkbox"
                    checked={form.taxable}
                    onChange={(e) => update({ taxable: e.target.checked })}
                    disabled={!canEdit}
                    className="h-4 w-4 accent-[#00b4d8]"
                  />
                  <Label htmlFor="taxable">TPS et TVQ</Label>
                </div>
              </div>
            </div>
            <dl className="sm:w-64 rounded-xl bg-[#f8fafc] p-4 space-y-1.5 text-sm" data-testid="estimate-totals">
              <div className="flex justify-between"><dt className="text-slate-500">Sous-total</dt><dd>{formatCents(totals.subtotal)}</dd></div>
              {form.taxable && (
                <>
                  <div className="flex justify-between"><dt className="text-slate-500">TPS ({TPS_RATE_LABEL})</dt><dd>{formatCents(totals.tps)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">TVQ ({TVQ_RATE_LABEL})</dt><dd>{formatCents(totals.tvq)}</dd></div>
                </>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold text-[#1a3a5c]">
                <dt>Total</dt><dd data-testid="estimate-total">{formatCents(totals.total)}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" onClick={handlePreview} disabled={busy || (!estimate && !canEdit)} data-testid="estimate-preview">
              <FileText className="w-4 h-4 mr-2" />
              Aperçu PDF
            </Button>
            {canEdit && (
              <Button type="button" onClick={handleSave} disabled={busy} className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90" data-testid="estimate-save">
                Enregistrer
              </Button>
            )}
          </div>

          {canEdit && (
            <div className="rounded-xl border border-slate-200 p-4 space-y-3">
              <p className="font-semibold text-[#1a3a5c]">Envoyer au client</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Courriel</Label>
                  <Input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="client@exemple.com" data-testid="estimate-send-to" />
                </div>
                <div>
                  <Label>Message (optionnel)</Label>
                  <Input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} placeholder="Merci de votre confiance!" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={handleSend} disabled={busy} className="bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="estimate-send">
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer le PDF
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EstimateDialog;
